import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Package, MapPin, Clock, Ship as ShipIcon, AlertCircle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { SHIPMENT_STATUSES } from "../lib/brand";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function Track() {
  const [params, setParams] = useSearchParams();
  const [tn, setTn] = useState(params.get("tn") || "");
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const q = params.get("tn");
    if (!q) return;
    setTn(q);
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setShipment(null);
      try {
        const { data } = await api.get(`/track/${encodeURIComponent(q.trim().toUpperCase())}`);
        if (!cancelled) setShipment(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    if (!tn.trim()) return;
    setParams({ tn: tn.trim().toUpperCase() });
  };

  const currentStatusIdx = shipment ? SHIPMENT_STATUSES.indexOf(shipment.status) : -1;

  return (
    <div data-testid="track-page" className="min-h-[80vh] bg-[#F1F5F9]">
      <section className="bg-[#1E2A78] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700] mb-3">Track Shipment</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">Real-time tracking, end-to-end.</h1>
          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-2xl">
            <div className="flex-1 flex items-center gap-2 px-4 py-3 bg-white rounded-sm">
              <Search className="h-5 w-5 text-slate-400" />
              <input
                type="text"
                value={tn}
                onChange={(e) => setTn(e.target.value)}
                placeholder="Enter your tracking number"
                className="flex-1 bg-transparent outline-none font-mono text-sm text-[#0F172A] placeholder:text-slate-400 uppercase"
                data-testid="track-input"
              />
            </div>
            <button type="submit" className="px-6 py-3 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] uppercase tracking-wider text-sm" data-testid="track-submit">
              Track
            </button>
          </form>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="bg-white border border-slate-200 p-10 rounded-sm text-center" data-testid="track-loading">
              <div className="h-10 w-10 border-4 border-[#1E2A78] border-t-transparent rounded-full animate-spin mx-auto" />
              <div className="mt-4 text-slate-600">Looking up your shipment…</div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-white border border-red-200 p-8 rounded-sm flex items-start gap-4" data-testid="track-error">
              <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-red-700">No shipment found</div>
                <div className="text-sm text-slate-600 mt-1">{error}. Please double-check your tracking number, or contact us if you need help.</div>
              </div>
            </div>
          )}

          {shipment && !loading && (
            <div className="space-y-6" data-testid="track-result">
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <div className="bg-[#1E2A78] text-white p-6 grid sm:grid-cols-3 gap-6">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700]">Tracking #</div>
                    <div className="font-mono text-lg mt-1">{shipment.tracking_number}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700]">Service</div>
                    <div className="font-display font-bold text-lg mt-1">{shipment.service_type}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[#FFD700]">ETA</div>
                    <div className="font-display font-bold text-lg mt-1">{shipment.estimated_delivery || "TBD"}</div>
                  </div>
                </div>

                <div className="p-6 grid sm:grid-cols-2 gap-6 border-b border-slate-200">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#1E2A78] mt-1" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500">From</div>
                      <div className="font-display font-bold">{shipment.origin}</div>
                      <div className="text-sm text-slate-600">{shipment.sender_name}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#FFD700] mt-1" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500">To</div>
                      <div className="font-display font-bold">{shipment.destination}</div>
                      <div className="text-sm text-slate-600">{shipment.receiver_name}</div>
                    </div>
                  </div>
                </div>

                <div className="p-6 grid sm:grid-cols-3 gap-6 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Description</div>
                    <div className="mt-1 text-slate-800">{shipment.description || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Weight</div>
                    <div className="mt-1 text-slate-800">{shipment.weight_kg ? `${shipment.weight_kg} kg` : "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Created</div>
                    <div className="mt-1 text-slate-800">{fmtDate(shipment.created_at)}</div>
                  </div>
                </div>
              </div>

              {/* Progress timeline */}
              <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8">
                <h3 className="text-lg font-display font-bold text-[#0F172A] mb-6">Shipment Progress</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-8">
                  {SHIPMENT_STATUSES.map((s, i) => {
                    const completed = i < currentStatusIdx;
                    const active = i === currentStatusIdx;
                    return (
                      <div key={s} className="text-center">
                        <div className={`mx-auto h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          completed ? "bg-[#1E2A78] text-white" :
                          active ? "bg-[#FFD700] text-[#1E2A78] ring-4 ring-[#FFD700]/30" :
                          "bg-slate-200 text-slate-500"
                        }`}>
                          {completed ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                        </div>
                        <div className={`mt-2 text-[10px] leading-tight ${active || completed ? "text-[#0F172A] font-semibold" : "text-slate-400"}`}>
                          {s}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4 flex items-center gap-2"><Clock className="h-4 w-4" /> Event History</h4>
                <ol className="relative border-l-2 border-slate-200 pl-6 space-y-6">
                  {[...(shipment.events || [])].reverse().map((ev, idx) => (
                    <li key={`${ev.timestamp}-${ev.status}`} className="relative">
                      <div className={`absolute -left-[33px] h-5 w-5 rounded-full border-2 ${idx === 0 ? "bg-[#FFD700] border-[#FFD700]" : "bg-white border-[#1E2A78]"}`} />
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-display font-bold text-[#0F172A]">{ev.status}</span>
                        <span className="text-xs text-slate-500 font-mono">{fmtDate(ev.timestamp)}</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5" />
                        {ev.location}
                      </div>
                      {ev.note && <div className="text-sm text-slate-500 mt-1">{ev.note}</div>}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {!shipment && !loading && !error && (
            <div className="bg-white border border-slate-200 rounded-sm p-10 text-center" data-testid="track-empty">
              <Package className="h-12 w-12 text-slate-300 mx-auto" />
              <div className="mt-4 font-display font-bold text-[#0F172A]">Enter your tracking number to begin</div>
              <div className="mt-2 text-sm text-slate-500">Tracking numbers look like <span className="font-mono text-[#1E2A78]">AMP2602ABCD1234</span></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
